// HelpPage.js
// 使い方説明書ページ。日本語/英語の自動切替＋手動切替対応。
// /public/img のアイコン画像と組み合わせて各ツールを詳細解説する。
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './css/App.css';
import './css/HelpPage.css';

const detectInitialLang = () => {
	try {
		const saved = localStorage.getItem('helpPageLang');
		if (saved === 'ja' || saved === 'en') return saved;
	} catch {}
	const nav = (typeof navigator !== 'undefined' && (navigator.language || navigator.userLanguage)) || 'en';
	return String(nav).toLowerCase().startsWith('ja') ? 'ja' : 'en';
};

// ================================================================
// アイコン定数（public/img 配下の実ファイル名）
// 「使い方」ドキュメントから参照する全アイコンを一箇所にまとめる。
// ================================================================
const I = {
	login:        '/img/icon_login_b.png',
	logout:       '/img/icon_logout_b.png',
	search:       '/img/icon_search_b.png',
	user:         '/img/default_icon.png',
	settings:     '/img/icon_settings_b.png',
	layers:       '/img/icon_layers_b.png',
	marker:       '/img/icon_marker_b.png',
	pen:          '/img/icon_pen_b.png',
	shape:        '/img/icon_shape_b.png',
	eraser:       '/img/icon_eraser_b.png',
	palette:      '/img/icon_palette_b.png',
	undo:         '/img/icon_undo_b.png',
	redo:         '/img/icon_redo_b.png',
	upload:       '/img/icon_cloud_upload_b.png',
	chat:         '/img/icon_chat_b.png',
	share:        '/img/icon_share_b.png',
	print:        '/img/icon_print_b.png',
	screenshot:   '/img/icon_screenShot_b.png',
	datalist:     '/img/icon_datalist_b.png',
	favorite:     '/img/icon_favorite_b.png',
	favorite_on:  '/img/icon_favorite_true.png',
	copy:         '/img/icon_file_copy_b.png',
	back:         '/img/icon_arrow_back_b.png',
	circle:       '/img/icon_circle.png',
	line:         '/img/icon_polyline.png',
	measurement:  '/img/icon_measurement.png',
	grid:         '/img/icon_grid.svg',
	label:        '/img/icon_label.svg',
	check:        '/img/check_b.png',
	markerR:      '/img/marker_r.png',
	markerB:      '/img/marker_b.png',
	markerG:      '/img/marker_g.png',
	markerP:      '/img/marker_p.png',
	markerY:      '/img/marker_y.png',
	markerBlack:  '/img/marker_black.png',
	pinR:         '/img/marker_pin_r.png',
	mountain:     '/img/marker_kigou_mountain.png',
	bird:         '/img/marker_kigou_bird.png',
	flower:       '/img/marker_kigou_flower.png',
	water:        '/img/marker_kigou_water.png',
	mushroom:     '/img/marker_kigou_kinoko.png',
	food:         '/img/marker_kigou_food.png',
	hot:          '/img/marker_kigou_hot.png',
	warning:      '/img/marker_kigou_warning.png',
	num1:         '/img/marker_num_1.png',
	num2:         '/img/marker_num_2.png',
};

// ================================================================
// ヘルプ本文。日英両対応。
// 各 section: { id, title, body?, features?, sub? }
//   body:     文字列の段落配列
//   features: { icon?, title, body[] } の配列。アイコン付きツール一覧。
//   sub:      { title, body[] } の配列。下位セクション。
// ================================================================
const HELP_CONTENT = {
	ja: {
		pageTitle: 'CollaboMap 使い方ガイド',
		toggleLabel: 'English',
		backToTop: '前のページへ戻る',
		toc: '目次',
		sections: [
			{
				id: 'overview',
				title: '概要',
				body: [
					'CollaboMap は、ブラウザだけで使える共同編集対応の地図サービスです。地理院地図・OpenStreetMap・OpenTopoMap など豊富な背景タイルに、マーカー・写真・図形・手書き線・グリッド・ラベルなどを重ねて、自分用の調査地図や仲間と一緒に育てる地図を簡単に作成できます。',
					'スマートフォンとパソコンの両方で動作し、PWA としてホーム画面に追加することも可能です。地形の3D表示、磁北線・スケールバーつきの印刷／PDF出力、QR コードによる位置共有、リアルタイムの共同編集など、現場で使える機能を一通り備えています。',
					'本ガイドでは、画面上の各種アイコンと対応する機能を中心に、よくある操作の流れを順を追って説明します。',
				],
				image: { src: '/img/sample_640.png', alt: 'CollaboMap の地図画面サンプル' },
			},
			{
				id: 'login',
				title: 'ログインとアカウント',
				body: [
					'地図を作成・編集するにはログインが必要です。読み取り専用で公開されている地図は、ログインしなくても閲覧できます。',
				],
				features: [
					{
						icon: I.login,
						title: 'ログイン',
						body: [
							'ログインボタンを押すと、Google アカウントでサインインするか、匿名アカウントを作成するかを選べます。',
							'匿名ログインは登録不要で 30 日間有効です。長く使いたい場合や複数端末でデータを共有したい場合は Google アカウントでサインインしてください。匿名から Google アカウントへの「アカウントを永久化」も可能です。',
						],
					},
					{
						icon: I.logout,
						title: 'ログアウト',
						body: [
							'画面右上のログアウトアイコンでサインアウトします。匿名ログインの場合、一度ログアウトすると同じ匿名アカウントには戻れない点に注意してください（地図データはアカウント永久化していなければ 30 日後に消えます）。',
						],
					},
					{
						icon: I.user,
						title: 'ユーザー設定',
						body: [
							'プロフィールアイコンを押すと表示されるメニューから、表示名（地図上のチャットや所有者名に表示される名前）と「テーマカラー」を変更できます。テーマカラーは自分の手書き線や図形のデフォルト色として使われます。',
						],
					},
				],
			},
			{
				id: 'top',
				title: 'トップページ',
				body: [
					'トップページ（/top）は、最近更新された公開地図のカードが並ぶ入口です。検索バーから公開地図を素早く探せるほか、ログインしている場合はマイページや新規地図作成へすぐ移動できます。',
				],
				features: [
					{
						icon: I.search,
						title: '地図を検索',
						body: [
							'検索バーにキーワードを入れると、タイトル・タグ・所有者名・メモを横断して公開地図を検索します。スペース区切りで AND 検索（全部に一致する地図）になります。',
							'検索結果は更新時刻の新しい順に並び、「もっと見る」で次のページを読み込みます。タグをクリックすると同じタグを持つ地図に絞り込めます。',
						],
					},
					{
						icon: I.favorite,
						title: 'お気に入り',
						body: [
							'カードの星アイコンを押すとお気に入り登録され、マイページの「お気に入り」セクションに追加されます。もう一度押すと外れます。',
						],
					},
				],
			},
			{
				id: 'mypage',
				title: 'マイページ',
				body: [
					'マイページ（/mypage）はログインユーザー専用のホームです。「お気に入り」「自分の地図」「最近見た他人の地図」の3セクションに分かれて表示されます。',
				],
				features: [
					{
						buttonLabel: '新しい地図を作る',
						title: '新規地図を作成',
						body: [
							'「新しい地図を作る」ボタンから、タイトル・公開設定・共同編集権限・タグなどを指定して新しい地図を作成します。作成直後はあなただけが編集できる非公開状態がデフォルトです。',
						],
					},
					{
						icon: I.favorite,
						title: 'お気に入り／最近見た地図',
						body: [
							'マイページのカードからもお気に入り操作が可能です。「最近見た地図」には、自分以外の作成者が公開している地図のうち最近開いたものが表示されます。',
						],
					},
				],
			},
			{
				id: 'mapscreen',
				title: '地図画面の使い方',
				body: [
					'地図画面（/map/...）は、中央が広い地図表示エリア、左右に各種ツールバーが配置された CollaboMap のメイン画面です。地図はマウスや指でドラッグして移動、ピンチやマウスホイールで拡大縮小、Shift+ドラッグまたはタッチ2本指で回転・チルト（傾き）を変更できます。',
				],
				features: [
					{
						icon: I.search,
						title: '場所検索',
						body: [
							'地名・住所・施設名・郵便番号で地図を移動できます。緯度経度（例: 35.681236, 139.767125）を直接入力することも可能。Ctrl/Cmd+F でも開きます。',
							'検索結果が複数ある場合はリストから選択。選んだ位置にはピンが立ち、ピンをクリックするとマーカー追加に移れます。',
						],
					},
					{
						icon: I.settings,
						title: '設定',
						body: [
							'地図画面の各種表示を切り替えます。等高線オーバーレイ、グリッドラベル、地形の高さ強調倍率、現在地表示などを ON/OFF できます。',
						],
					},
					{
						icon: I.layers,
						title: 'レイヤー設定',
						body: [
							'背景地図とオーバーレイを切り替えます。標準地図／淡色地図／写真／陰影起伏図／赤色立体地図／傾斜量図／火山土地条件図など、地理院地図のさまざまなタイルに切り替え可能。',
							'各レイヤーは表示／非表示／不透明度を個別調整できます。「カスタムレイヤー追加」で URL を指定して任意の XYZ タイルも追加できます。',
						],
					},
					{
						icon: I.marker,
						title: 'マーカー追加',
						body: [
							'クリックした位置にマーカーを追加します。スマホでは長押しでも追加できます。アイコン・名前・ふりがな・メモを入力し、写真添付や標高自動取得が可能です。',
							'マーカーをクリックすると編集ポップアップが開き、移動・複製・削除・写真の追加もできます。',
						],
					},
					{
						icon: I.upload,
						title: 'ファイルアップロード',
						body: [
							'写真、PDF、動画など任意のファイルを地図上に配置できます。位置情報（EXIF GPS）が付いた写真は自動的に撮影地点に配置されます。',
							'ZIP ファイルもアップロード可能で、まとめてマーカー化できます。',
						],
					},
					{
						icon: I.pen,
						title: 'ペン（フリーハンド）',
						body: [
							'地図上にフリーハンドの線を描き込みます。色・太さ・透明度はパレットで設定。Apple Pencil・スタイラス・マウス・指のいずれでも描けます。',
							'描画中はピンチで地図ズームが効くため、細かいトレースもしやすくなっています。',
						],
					},
					{
						icon: I.shape,
						title: '図形',
						body: [
							'円・直線・距離計測・グリッド・ラベルなどの幾何図形を作成するツール群を開きます。詳細は「編集機能」の章を参照してください。',
						],
					},
					{
						icon: I.eraser,
						title: '消しゴム',
						body: [
							'手書き線や図形を削除します。スワイプした部分だけを部分的に消せます。',
						],
					},
					{
						icon: I.palette,
						title: 'パレット（線設定）',
						body: [
							'手書き線や図形の現在色・線幅・透明度を変更します。よく使う色のスウォッチを画面下に並べて素早く切り替えできます。',
						],
					},
					{
						icon: I.undo,
						title: 'アンドゥ',
						body: [
							'直前の追加・移動・削除を取り消します。連続して押すとさらに過去の操作を戻せます。Ctrl/Cmd+Z でも実行可能。',
						],
					},
					{
						icon: I.redo,
						title: 'リドゥ',
						body: [
							'アンドゥで取り消した操作をやり直します。連続して押せば複数回分まとめて再適用できます。Ctrl/Cmd+Y でも実行可能。',
							'描画・図形・マーカーの追加／削除／編集／移動に対応しています。新たに編集操作を行うと、それ以降のリドゥ履歴は破棄されます。',
						],
					},
					{
						icon: I.chat,
						title: 'チャット',
						body: [
							'同じ地図を開いている他ユーザーとリアルタイムにテキストチャットできます。共同調査の連絡や場所のメモ書きに使えます。',
						],
					},
					{
						icon: I.share,
						title: '共有',
						body: [
							'地図の URL を発行・コピーします。「自分のみ」「閲覧のみ」「共同編集」を切り替えて公開範囲を設定できます。共同編集を有効にすると、URL を知っている人がリアルタイムに編集に参加できます。',
						],
					},
					{
						icon: I.print,
						title: '印刷／PDF',
						body: [
							'印刷ダイアログを開きます。詳細は「印刷／PDF出力」のセクションを参照してください。',
						],
					},
					{
						icon: I.screenshot,
						title: 'スクリーンショット',
						body: [
							'現在の地図表示の高解像度スクリーンショットを生成し PNG として保存します。マーカー・手書きを含む見たままの状態で保存されます。',
						],
					},
					{
						icon: I.datalist,
						title: 'データ一覧',
						body: [
							'地図に登録されたマーカーや写真などを一覧表示します。検索・並び替え・複数選択削除・CSV エクスポートが可能です。',
							'一覧の最後には「マーカー+写真：12/150」「ファイル：3/50」のように、現在の登録件数と上限が表示されます。',
							'作成者以外が開いたときに「索引ファイルから読み込み（日時）」と出る場合は、作成者が作った索引ファイルからまとめて読み込んでいます。その日時以降の変更はリアルタイムで反映されます。',
						],
					},
					{
						icon: I.copy,
						title: '地図をコピー',
						body: [
							'地図画面のコピーボタンから、表示中の地図を自分のアカウントに複製できます。元の地図のマーカー・図形・写真・添付ファイルがすべて引き継がれ、新しい ID で「○○_copy」というタイトルになります。',
							'コピーされた地図は非公開設定で作成されるため、必要に応じて共有設定を変更してください。',
						],
					},
					{
						icon: I.back,
						title: '戻る',
						body: [
							'マイページ／トップページに戻ります。フルスクリーン中はフルスクリーンを抜ける動作になります。',
						],
					},
				],
				sub: [
					{
						title: 'キーボードショートカット',
						body: [
							'B：ペンツール',
							'E：消しゴム',
							'S：図形モード切替',
							'L：直線ツール',
							'C：円ツール',
							'K：計測ツール',
							'G：グリッドツール',
							'A：ラベルツール',
							'P：カラーパレット',
							'M：マーカー追加',
							'D：3D 地形切替',
							'Ctrl/Cmd+Z：元に戻す',
							'Ctrl/Cmd+Y：やり直し（リドゥ）',
							'Ctrl/Cmd+C：マーカーコピー',
							'Ctrl/Cmd+V：マーカー貼り付け／コピーした写真の登録（EXIF の位置情報があれば撮影地点に配置）',
							'?：このショートカット一覧を表示',
						],
					},
				],
			},
			{
				id: 'sharing',
				title: '共有範囲とサムネイル',
				body: [
					'CollaboMap の地図は、所有者以外がどこまで触れるかを「共有範囲」で細かく制御できます。また、地図のカード表示や OGP に使われる「サムネイル画像」を自分で生成・更新できます。これらは地図画面右上の共有メニュー、または地図設定（編集）ダイアログから設定します。',
				],
				features: [
					{
						icon: I.share,
						title: '共有範囲（Permission）',
						body: [
							'共有範囲は4段階あり、目的に応じて選択します。それぞれの違いは次のとおりです。',
							'【自分のみ（Private）】：所有者本人だけが閲覧・編集できます。トップページの検索結果や他人の「最近見た地図」には現れません。下書き段階の地図や個人メモにおすすめです。',
							'【閲覧のみ（View only）】：URL を知っているユーザーは地図を「読み取り専用」で見られます。新規マーカーや手書きの追加はできず、既存データの変更も不可。完成版の調査資料や、特定メンバーへの限定公開に向きます。',
							'【共同編集（Joint editing）】：URL を知っているユーザーが自由にマーカー・写真・手書き・図形を追加・編集できます。各ユーザーのデータは独立して保存されるため、同時編集してもデータ衝突は発生しません。フィールドワークやワークショップに最適。',
							'【検索可（Searchable）】：上記の「閲覧のみ」または「共同編集」と組み合わせる ON/OFF オプション。ON にするとトップページの検索結果や「最近の公開地図」一覧に表示されるようになります。OFF のままなら URL を知っている人にしか見つかりません。',
							'共有範囲はいつでも変更できます。「自分のみ」に戻すと、以前 URL を共有していた人もアクセスできなくなります（URL を踏むと権限なし表示）。',
						],
					},
					{
						icon: I.copy,
						title: 'コピー可否（Copyable）',
						body: [
							'共有範囲とは別に、「他人がこの地図を自分のアカウントへコピーできるか」のフラグを設定できます。OFF にすると、他ユーザーは閲覧できてもコピー複製はできません。教材・有料コンテンツの保護などに使えます。',
							'自分の地図は常にコピー可能です（自分のアカウント内への複製）。',
						],
					},
					{
						icon: I.chat,
						title: '共同編集中の権限とデータ分離',
						body: [
							'共同編集モードで複数人がアクセスしても、各ユーザーのデータは内部的に「自分のレイヤー」として独立保存されます。手書き線や図形は描画者本人だけが消しゴムで削除でき、他人の手書きは触れません。',
							'マーカーは「所有者」が記録され、移動・削除はそのマーカーを作成したユーザーだけが行えます。所有者以外は閲覧と自分が追加した写真の操作に限定されます。',
							'チャットは共同編集中の参加者全員に届きます。場所のメモや作業連絡に活用できます。',
						],
					},
				],
				sub: [
					{
						title: 'サムネイル生成（地図カード用画像）',
						body: [
							'トップページやマイページのカード、SNS シェア時の OGP 画像に使われる「サムネイル」を、地図設定ダイアログ内の「サムネイル作成」ボタンから生成できます。',
							'生成は現在の地図表示（背景・マーカー・手書き・図形）をそのままキャプチャする方式で、ボタンを押した瞬間の見た目がそのままサムネイルになります。事前に「カードに載せたい画角」までズーム・パンしてからボタンを押してください。',
							'サムネイルは JPEG として地図に紐付いて保存され、マイページ／トップページのカードに即座に反映されます。差し替えたい時は何度でも再生成可能です。',
							'非公開地図でもサムネイルは生成できますが、自分以外には表示されません。検索可に変更すると、他のユーザーのカードでも同じサムネイルが見えるようになります。',
							'写真マーカーが多い地図では、サムネイル生成と合わせて各写真のサムネイル画像も自動生成されます。これによりカードや一覧表示でも軽量に画像を表示できます。',
						],
					},
					{
						title: 'スクリーンショットとの違い',
						body: [
							'地図画面右側の「スクリーンショット」アイコンは、現在の表示を PNG として手元に保存する機能で、地図のサムネイルは更新しません。',
							'カード画像を変えたい場合は必ず「サムネイル作成」を、画像をローカル保存したい場合は「スクリーンショット」を使います。',
						],
					},
					{
						title: 'タグと検索',
						body: [
							'地図設定ではタグ（スペースまたはカンマ区切り）を登録できます。検索可の地図ではトップページからタグ検索でき、カードのタグをクリックすると同じタグを持つ地図に絞り込めます。',
							'非公開でもタグを付けておけば、マイページ内で整理用に使えます。',
						],
					},
				],
			},
			{
				id: 'editing',
				title: '編集機能',
				body: [
					'CollaboMap では、地図のうえに「マーカー」「手書き」「図形」「ラベル」「写真／ファイル」を重ねて配置・編集できます。それぞれのツールについて詳しく説明します。',
				],
				features: [
					{
						icon: I.marker,
						title: 'マーカーの追加と編集',
						body: [
							'マーカーは1つの位置情報に対して、アイコン・名前・ふりがな・メモ・写真・標高を関連付けることができる基本要素です。',
							'追加方法：左ツールバーのマーカーアイコンを押してから地図をクリック／タップする、または検索結果から「ここにマーカー追加」を選びます。',
							'アイコンは色付き丸（紫・青・緑・黄・赤・黒）、ピン型、記号（家・水・山・警告・停止・魚・車・飛行機・電車・動物・温泉・自転車・キノコ・鳥・山菜・テント・花・食べ物・トイレ・滝・ポスト・パドル・ベンチ・バス停）、文字（P・S・A・T・G・C）、数字（1〜6）から選べます。',
							'名前の先頭に絵文字を入れると、その絵文字がアイコンの上に表示される特殊モードになります。',
							'標高は地理院標高 API から自動取得され、海外では Terrarium タイルから計算されます。',
							'写真添付：マーカー編集中に写真を選ぶと、サムネイルがマーカー直下に重ね表示されます。複数枚にも対応。',
							'移動：編集ポップアップの「移動」を押すか、所有者ならドラッグで位置を変更できます。',
						],
					},
					{
						icon: I.pen,
						title: '手書き（フリーハンド）',
						body: [
							'ペンツールを ON にして地図をドラッグすると、現在のパレット色・太さで線が描けます。',
							'Alt キーを押しながらでも描画モードに入れます（PC のみ）。スマホ／タブレットでは指やスタイラスで直接描画。',
							'1 ストロークごとにアンドゥ単位として記録され、Ctrl/Cmd+Z で1本ずつ取り消せます。',
							'共同編集中、自分の手書きは即座に他ユーザーの画面にも反映されます。',
							'描画した線データはユーザーごとに個別保存されるため、他人の手書きと混ざることはありません。',
						],
					},
					{
						icon: I.eraser,
						title: '消しゴム',
						body: [
							'消しゴムツールに切り替えて地図をなぞると、自分が描いた線・図形を消去します（他人の手書き・図形は消せません）。',
							'部分消し：手書き線は塗った部分だけを消すことができ、線が複数のセグメントに分割されます。',
							'消した内容もアンドゥで復元できます。',
						],
					},
					{
						icon: I.circle,
						title: '円ツール',
						body: [
							'円の中心と半径を指定して円を描きます。半径はメートル単位で計算されるため、ズーム値や緯度に依存しない地理的な大きさで描画されます。',
							'2点目をドラッグ／タップで仮置きしたあと、ポップアップで半径（m）を直接入力して微調整可能。',
							'用途例：撮影禁止区域の半径表示、避難範囲、見通し範囲、調査エリア。',
						],
					},
					{
						icon: I.line,
						title: '直線（折れ線）',
						body: [
							'クリックで点を打ち、最後にダブルクリック／確定ボタンで折れ線が確定します。直線は単純な2点線、または複数点の折れ線として扱えます。',
							'各セグメントの距離・累積距離・累積標高差（登り／下り）が表示され、簡易的なルート設計に使えます。',
						],
					},
					{
						icon: I.measurement,
						title: '距離計測',
						body: [
							'複数点を順にクリックすると、その経路の総距離をリアルタイム表示します。',
							'計測結果はラベルとして地図に残せます。登山・サイクリング・建設現場の距離取りなどに便利です。',
						],
					},
					{
						icon: I.grid,
						title: 'グリッド',
						body: [
							'四角形で範囲を指定すると、その内部を等分割（横 N 等分）または距離分割（X メートル間隔）でメッシュに区切れます。',
							'区画ラベル（A1・B2 など）を自動付与でき、現地調査の参照番号として使えます。',
							'グリッド線の色・太さ・透明度・ラベル文字サイズはダイアログ上で個別設定可能です。',
						],
					},
					{
						icon: I.label,
						title: 'ラベル',
						body: [
							'地図上の任意の位置に文字ラベルを置けます。フォントサイズ・色・縁取り（ハロー）色を細かく調整可能。',
							'複数行のラベルも作れます。入力欄で Enter を押すと改行、Shift+Enter（または Ctrl/Cmd+Enter）で確定します。',
							'ラベル単独のほか、円・直線・グリッドの注釈として使えます。',
						],
					},
					{
						icon: I.upload,
						title: '写真とファイル',
						body: [
							'写真は EXIF の位置情報があれば自動でその位置に配置されます。位置情報がない場合は画面中心に配置されるので、ドラッグで正しい場所に移動してください。',
							'エクスプローラー / Finder で写真ファイルをコピーして地図上で Ctrl/Cmd+V を押しても登録できます（EXIF の位置情報を読み取って撮影地点に配置）。',
							'PDF・テキスト・動画など任意のファイルもマーカー型で配置でき、クリックでダウンロード／プレビューできます。',
							'写真は EXIF 撮影日時を「Shooting」欄に保持し、データ一覧で日付ソートが可能です。',
						],
					},
					{
						icon: I.datalist,
						title: '登録数の上限',
						body: [
							'1 つの地図に登録できる件数には上限があります。マーカーと写真は合わせて 150 件まで、アップロードしたファイル（GPX トラック・KML・GeoJSON・ZIP など）は 50 件までです。',
							'マーカー・写真の枠とファイルの枠は独立しています。マーカーや写真が 150 件に達していても、トラックなどのファイルは 50 件まで登録できます。',
							'手書き・図形・ラベルはこの件数に含まれません。ユーザーごとにまとめて保存されるため、何本描いても上限を消費しません。',
							'上限に達するとマーカーの追加やアップロードができなくなり、メッセージが表示されます。GPX のウェイポイントや写真をまとめて取り込む場合も、上限までで打ち切られます。',
							'現在の件数はデータ一覧で確認できます。上限に達したときは、不要なマーカー・写真・ファイルを削除してください。',
						],
					},
					{
						icon: I.print,
						title: '印刷／PDF出力',
						body: [
							'印刷ダイアログでは紙サイズ（A3/A4/B4/B5）、向き（縦／横）、縮尺（1:10,000〜1:200,000、任意）、スケールバー位置、カラー（カラー／モノクロ／モノ＋赤）、磁北線本数と色、QRコード位置（右下／左下／なし）、マーカー印刷モード（なし／そのまま／ひし形に変更）、マーカーサイズ（75 / 100 / 150 %）、文字サイズ調整を細かく指定できます。',
							'地図上の青枠が印刷範囲のプレビューを兼ね、ドラッグして中心位置を調整できます。',
							'印刷物には、左上にタイトル・所有者・メモ、右上に縮尺と用紙サイズ、右下／左下に QR コード（クリック地点へジャンプする URL）、用紙端には地図のクレジット（地理院・OSM など）が自動的に印字されます。',
							'生成は高解像度（DPI 300相当）で行われ、A3 大判でも線がぼやけません。',
						],
					},
					{
						icon: I.share,
						title: '共有設定',
						body: [
							'共有設定ダイアログから「自分のみ（プライベート）」「閲覧のみ（URL を知っている人は閲覧可）」「共同編集（URL を知っている人は編集可）」「公開検索可（トップページの検索結果に出現）」を切り替えられます。',
							'共同編集モードでは複数人が同時に編集してもデータ衝突は起きません（各ユーザーの手書き・マーカーは独立して保存）。',
						],
					},
					{
						icon: I.chat,
						title: 'チャット',
						body: [
							'同じ地図を開いているメンバー間でリアルタイムテキストチャットができます。簡単な現場連絡や、場所のメモ代わりに使えます。',
						],
					},
				],
			},
			{
				id: 'qa',
				title: 'よくある質問 (Q&A)',
				body: [],
				sub: [
					{
						title: 'Q. 匿名ログインのデータはいつ消えますか？',
						body: ['A. 最終ログインから 30 日経過すると自動で削除されます。Google アカウントでサインインし直す（アカウントを永久化）と、それまでのデータをそのまま引き継げます。'],
					},
					{
						title: 'Q. 地図がオフラインでも見られますか？',
						body: ['A. PWA としてインストールすると、最近表示したタイル範囲はキャッシュされて、オフラインでもある程度閲覧可能です。新規データの保存にはオンラインが必要です。'],
					},
					{
						title: 'Q. 編集操作を一つ前に戻したい',
						body: ['A. 直前の操作はアンドゥボタン（Ctrl/Cmd+Z）で取り消せます。複数回押すとさらに前の状態に戻せます。取り消し過ぎたときはリドゥボタン（Ctrl/Cmd+Y）でやり直せます。'],
					},
					{
						title: 'Q. 印刷時にマーカー見やすくしたい',
						body: ['A. 印刷ダイアログの「マーカー印刷」を「ひし形に変更」にすると、各マーカーをひし形アイコンに統一できます。「マーカーサイズ」で大きさも 75/100/150 % から選べます。カラー設定が「モノ＋赤」のときは赤ひし形、それ以外は元のアイコン番号 mod 6 の色のひし形が使われます。なお写真マーカーは、ひし形にしても内容が分からないため印刷されません。'],
					},
					{
						title: 'Q. 共同編集で他人の編集と衝突しませんか？',
						body: ['A. 各データはユーザーごとに独立して保存されるため、同じ場所を同時に編集しても衝突は起きません。リアルタイムに相手の編集が地図に反映されます。'],
					},
					{
						title: 'Q. 写真の位置がずれている',
						body: ['A. EXIF GPS が無い、または不正確な場合は画面中心に置かれます。マーカー編集の「移動」ボタンを押してから正しい位置をタップすれば動かせます。'],
					},
					{
						title: 'Q. 地形が立体的に見えるようにしたい',
						body: ['A. 地図画面右側の 2D/3D ボタンで切り替えられます。「設定」で地形強調倍率も変えられます。'],
					},
					{
						title: 'Q. マーカーやファイルが追加できなくなった',
						body: ['A. 登録数の上限に達しています。マーカーと写真は合計 150 件、アップロードファイル（トラックなど）は 50 件までで、それぞれ別枠です。データ一覧で現在の件数を確認し、不要なデータを削除してください。手書き・図形・ラベルは件数に含まれません。'],
					},
				],
			},
		],
	},
	en: {
		pageTitle: 'CollaboMap User Guide',
		toggleLabel: '日本語',
		backToTop: 'Back',
		toc: 'Table of Contents',
		sections: [
			{
				id: 'overview',
				title: 'Overview',
				body: [
					'CollaboMap is a browser-based collaborative map service. Layer markers, photos, shapes, freehand strokes, grids and labels on top of a wide range of background tiles (GSI, OpenStreetMap, OpenTopoMap, …) to build personal field maps or maps you grow together with friends.',
					'It works on smartphones and PCs, and can be installed as a PWA. 3D terrain, magnetic-north / scale-bar print output, QR code position sharing, real-time collaborative editing — every feature you want in the field is built in.',
					'This guide walks through each tool icon you see on screen and explains the typical workflow step by step.',
				],
				image: { src: '/img/sample_640.png', alt: 'Sample CollaboMap screen' },
			},
			{
				id: 'login',
				title: 'Sign-in & accounts',
				body: [
					'A sign-in is required to create or edit a map. Public read-only maps are viewable without signing in.',
				],
				features: [
					{
						icon: I.login,
						title: 'Sign in',
						body: [
							'The sign-in button lets you choose a Google account or an anonymous account.',
							'Anonymous accounts require no registration and last 30 days. To keep data longer or use it from multiple devices, sign in with Google. You can also "make permanent" an anonymous account by linking it to a Google account.',
						],
					},
					{
						icon: I.logout,
						title: 'Sign out',
						body: [
							'Click the sign-out icon at the top right. With an anonymous account, signing out means you can never get back into the same anonymous account, so any unlinked data will be deleted in 30 days.',
						],
					},
					{
						icon: I.user,
						title: 'User settings',
						body: [
							'Click your profile icon to edit your display name (shown in chat and as the owner name) and theme color. The theme color is the default color used for your strokes and shapes.',
						],
					},
				],
			},
			{
				id: 'top',
				title: 'Top page',
				body: [
					'The top page (/top) is the entry hall, with cards of the most recently updated public maps. Use the search bar to find public maps quickly, or jump to your My Page or new-map dialog if you are signed in.',
				],
				features: [
					{
						icon: I.search,
						title: 'Search maps',
						body: [
							'Type a keyword in the search bar to search public maps across title, tags, owner name and memo. Multiple words separated by spaces become an AND search.',
							'Results are sorted by latest update; the "More…" button loads the next page. Click any tag to filter by that tag.',
						],
					},
					{
						icon: I.favorite,
						title: 'Favorite',
						body: [
							'Click the star on a card to favorite it (it appears in the Favorites section of My Page). Click again to remove.',
						],
					},
				],
			},
			{
				id: 'mypage',
				title: 'My page',
				body: [
					'My Page (/mypage) is your personal home, divided into "Favorites", "My maps" and "Recently seen maps of others".',
				],
				features: [
					{
						buttonLabel: 'New map',
						title: 'New map',
						body: [
							'The "New map" button opens a dialog where you set title, sharing, collaboration permissions and tags. Newly created maps default to private (only you can edit).',
						],
					},
					{
						icon: I.favorite,
						title: 'Favorites & recent maps',
						body: [
							'You can also favorite from My Page cards. "Recently seen maps" lists public maps owned by other users that you have recently opened.',
						],
					},
				],
			},
			{
				id: 'mapscreen',
				title: 'Map screen',
				body: [
					'The map screen (/map/...) is the main canvas, with a large map area and toolbars on each side. Drag with mouse or finger to pan, scroll/pinch to zoom, Shift+drag or two-finger drag to rotate and tilt.',
				],
				features: [
					{
						icon: I.search,
						title: 'Place search',
						body: [
							'Search by place name, address, POI or postal code, or paste latitude/longitude (e.g. 35.681236, 139.767125). Ctrl/Cmd+F also opens the search.',
							'A pin appears at the chosen location; click it to add a marker there.',
						],
					},
					{
						icon: I.settings,
						title: 'Settings',
						body: [
							'Toggle on-map UI: contour overlay, grid labels, terrain exaggeration, current-location indicator, and more.',
						],
					},
					{
						icon: I.layers,
						title: 'Layers',
						body: [
							'Switch the background and overlays. Choose from GSI standard / pale / aerial / hillshade / red-relief / slope / volcanic land condition maps and many overlays. Each layer can be toggled and its opacity adjusted.',
							'Add custom layers by entering an XYZ tile URL.',
						],
					},
					{
						icon: I.marker,
						title: 'Add marker',
						body: [
							'Click (or long-press on mobile) to drop a marker. Set icon, name, reading, note, and optionally attach photos. Elevation is auto-fetched.',
							'Click an existing marker to open the popup with move, copy and delete actions.',
						],
					},
					{
						icon: I.upload,
						title: 'Upload file',
						body: [
							'Upload photos, PDFs, videos or any file. EXIF-tagged photos drop at the photographed location automatically. ZIP archives can be expanded and turned into multiple markers at once.',
						],
					},
					{
						icon: I.pen,
						title: 'Pen (freehand)',
						body: [
							'Draw freehand strokes at the current palette color/width. Works with Apple Pencil, stylus, mouse or finger.',
							'You can still pinch-zoom while in pen mode for fine tracing.',
						],
					},
					{
						icon: I.shape,
						title: 'Shapes',
						body: [
							'Open the shape sub-tools: circle, line, distance, grid, label. See the Editing section for details.',
						],
					},
					{
						icon: I.eraser,
						title: 'Eraser',
						body: [
							'Deletes handwritten lines and shapes. You can partially erase only the area you swipe.',
						],
					},
					{
						icon: I.palette,
						title: 'Palette',
						body: [
							'Set color, line width and opacity for strokes and shapes. Frequently-used color swatches are docked at the bottom for quick switching.',
						],
					},
					{
						icon: I.undo,
						title: 'Undo',
						body: [
							'Undo your last action. Press repeatedly to step further back. Ctrl/Cmd+Z works as well.',
						],
					},
					{
						icon: I.redo,
						title: 'Redo',
						body: [
							'Re-applies actions that were just undone. Press repeatedly to redo multiple steps. Ctrl/Cmd+Y also works.',
							'Supports drawings, shapes and marker add/delete/edit/move. Performing a new edit clears the redo history beyond that point.',
						],
					},
					{
						icon: I.chat,
						title: 'Chat',
						body: [
							'Real-time text chat with other users viewing the same map. Useful for field communication or quick note-taking.',
						],
					},
					{
						icon: I.share,
						title: 'Share',
						body: [
							'Generate / copy a map URL. Switch sharing between Private / View-only / Joint editing / Searchable.',
							'In joint-editing mode, anyone with the URL can edit in real time.',
						],
					},
					{
						icon: I.print,
						title: 'Print / PDF',
						body: [
							'Open the print dialog. See the Print section in Editing for full details.',
						],
					},
					{
						icon: I.screenshot,
						title: 'Screenshot',
						body: [
							'Capture the current map view as a high-resolution PNG, including markers and drawings.',
						],
					},
					{
						icon: I.datalist,
						title: 'Data list',
						body: [
							'List all markers, photos and files on the map. Search, sort, multi-select delete and CSV export are supported.',
							'The bottom of the list shows the current counts against their limits, e.g. "Markers+photograph: 12/150" and "File: 3/50".',
							'When someone other than the owner opens the map, "Loaded from the index file (time)" may appear: items were loaded in one go from the index file the owner generated. Changes after that time are applied live.',
						],
					},
					{
						icon: I.copy,
						title: 'Copy this map',
						body: [
							'Use the copy button on the map screen to clone the currently open map into your account. Markers, shapes, photos and attached files are all carried over, with a new ID and "..._copy" appended to the title.',
							'Copies are created as private; change sharing afterwards if needed.',
						],
					},
					{
						icon: I.back,
						title: 'Back',
						body: [
							'Returns to My Page or the top page. While in fullscreen, exits fullscreen first.',
						],
					},
				],
				sub: [
					{
						title: 'Keyboard shortcuts',
						body: [
							'B: pen tool',
							'E: eraser',
							'S: toggle shape mode',
							'L: line tool',
							'C: circle tool',
							'K: measurement tool',
							'G: grid tool',
							'A: label tool',
							'P: color palette',
							'M: add marker',
							'D: toggle 3D terrain',
							'Ctrl/Cmd+Z: undo',
							'Ctrl/Cmd+Y: redo',
							'Ctrl/Cmd+C: copy marker',
							'Ctrl/Cmd+V: paste marker, or register a copied photo (EXIF GPS places it at the shot location)',
							'?: show this shortcut list',
						],
					},
				],
			},
			{
				id: 'sharing',
				title: 'Sharing scope & thumbnail',
				body: [
					'CollaboMap lets you finely control how others can interact with each map through "sharing scope", and you can generate or refresh the "thumbnail" used for cards and OGP images. Both are managed via the share menu and the map settings (edit) dialog.',
				],
				features: [
					{
						icon: I.share,
						title: 'Sharing scope (Permission)',
						body: [
							'There are four sharing levels:',
							'[Private]: only the owner can view or edit. The map never appears in search results or the "recently seen" list of others. Best for drafts or personal notes.',
							'[View only]: anyone with the URL can read it but cannot add or modify anything. Suitable for finished survey reports or limited-distribution maps.',
							'[Joint editing]: anyone with the URL can freely add or edit markers, strokes, shapes and photos. Each user\'s data is stored independently so simultaneous edits never conflict. Great for fieldwork or workshops.',
							'[Searchable]: an ON/OFF flag combined with View-only or Joint-editing. When ON the map appears in the top page search results and "recent public maps" list. When OFF only people with the URL can find it.',
							'You can change sharing at any time. Setting it back to Private revokes access from anyone who previously had the URL.',
						],
					},
					{
						icon: I.copy,
						title: 'Copyable',
						body: [
							'Independent of sharing scope, you can flag whether others may copy this map into their own account. With copy disabled, viewers cannot duplicate it. Useful for protecting course material or paid content.',
							'Your own maps are always copyable into your own account.',
						],
					},
					{
						icon: I.chat,
						title: 'Permissions and data isolation in joint editing',
						body: [
							'In joint-editing mode each user\'s data is internally stored as their own layer. Only the original creator can erase their strokes/shapes; other users\' drawings are untouchable.',
							'Markers carry an owner; only the owner can move or delete them. Other users may attach their own photos but cannot edit the marker itself.',
							'Chat reaches all participants on the map.',
						],
					},
				],
				sub: [
					{
						title: 'Thumbnail generation (the card image)',
						body: [
							'The "Create thumbnail" button in the map settings dialog generates the image used by My Page / top page cards and SNS OGP previews.',
							'It captures the current map view (background, markers, drawings, shapes) at the moment you press the button. Zoom and pan to the angle you want shown on cards before pressing it.',
							'The thumbnail is saved as a JPEG attached to the map and is reflected on cards immediately. Regenerate as often as you like.',
							'Even private maps can have thumbnails, but no one else sees them. They become visible on others\' cards once you switch to Searchable.',
							'For maps with many photo markers, individual photo thumbnails are also generated alongside the map thumbnail, so card and list views stay lightweight.',
						],
					},
					{
						title: 'Difference from screenshot',
						body: [
							'The screenshot icon on the right toolbar saves the current view as a PNG to your local device — it does NOT update the map\'s thumbnail.',
							'Use "Create thumbnail" to change the card image, and "Screenshot" to save a local copy.',
						],
					},
					{
						title: 'Tags & search',
						body: [
							'You can attach tags (space- or comma-separated) in the settings dialog. Searchable maps appear in tag searches on the top page; clicking a card\'s tag filters maps by that tag.',
							'Tags work for organizing private maps within My Page as well.',
						],
					},
				],
			},
			{
				id: 'editing',
				title: 'Editing features',
				body: [
					'CollaboMap lets you place "markers", "freehand strokes", "shapes", "labels" and "photos / files" on top of the map. Each tool is described in detail below.',
				],
				features: [
					{
						icon: I.marker,
						title: 'Markers',
						body: [
							'A marker associates an icon, name, reading, note, photo and elevation with a single location.',
							'How to add: click the marker icon then click on the map (long-press on mobile), or use "Add marker here" from a search result.',
							'Icons available: colored circles (purple/blue/green/yellow/red/black), pins, symbols (house, water, mountain, warning, stop, fish, car, plane, train, animal, hot-spring, bicycle, mushroom, bird, mountain food, tent, flower, food, toilet, waterfall, post, paddle, bench, bus stop), letters (P/S/A/T/G/C), numbers (1–6).',
							'Starting the name with an emoji enters a special mode that draws the emoji on top of the icon.',
							'Elevation is fetched from the GSI elevation API; the Terrarium tileset is used outside Japan.',
							'Photo attachment: pick photos in the marker popup; thumbnails appear stacked under the marker.',
							'Move: use the "Move" button or drag the marker (owner only).',
						],
					},
					{
						icon: I.pen,
						title: 'Freehand pen',
						body: [
							'Drag on the map in pen mode to draw at the current palette color/width.',
							'Hold Alt to enter drawing mode on PC. On mobile, draw with finger/stylus.',
							'Each stroke is one undo unit; Ctrl/Cmd+Z removes them one by one.',
							'During collaboration your strokes appear on other users\' screens immediately.',
							'Strokes are stored per-user, so they never collide with other users\'.',
						],
					},
					{
						icon: I.eraser,
						title: 'Eraser',
						body: [
							'Erases only your own strokes/shapes (others\' drawings cannot be erased).',
							'Strokes can be partially erased, splitting them into multiple segments.',
							'Erased data can also be restored with undo.',
						],
					},
					{
						icon: I.circle,
						title: 'Circle',
						body: [
							'Place a center then drag/tap a second point to set the radius. The radius is calculated in meters, so the geographic size is independent of the zoom level.',
							'Use the popup to type an exact radius in meters.',
							'Examples: no-photo zones, evacuation perimeters, line-of-sight ranges, survey areas.',
						],
					},
					{
						icon: I.line,
						title: 'Line / polyline',
						body: [
							'Click points; double-click or confirm to finalize. Each segment shows distance, cumulative distance and elevation gain/loss — handy for quick route planning.',
						],
					},
					{
						icon: I.measurement,
						title: 'Distance measurement',
						body: [
							'Click a series of points to see the running total distance.',
							'Save the measurement as a label on the map. Useful for hiking, cycling, construction surveys.',
						],
					},
					{
						icon: I.grid,
						title: 'Grid',
						body: [
							'Define a rectangle and split it into N equal divisions or by fixed distance (X meters).',
							'Auto-generated cell labels (A1, B2, …) make great reference IDs for field surveys.',
							'Color, width, opacity and label size are configurable.',
						],
					},
					{
						icon: I.label,
						title: 'Label',
						body: [
							'Place a text label anywhere. Adjust font size, color, halo color and outline.',
							'Labels can span multiple lines: press Enter for a new line, and Shift+Enter (or Ctrl/Cmd+Enter) to confirm.',
							'Use stand-alone, or as annotation alongside circles / lines / grids.',
						],
					},
					{
						icon: I.upload,
						title: 'Photos & files',
						body: [
							'EXIF-located photos drop automatically at the right place; otherwise they appear at the screen center for you to drag.',
							'You can also copy a photo file in Explorer / Finder and press Ctrl/Cmd+V on the map to register it (EXIF GPS places it at the shot location).',
							'Any file (PDF, text, video, …) can be placed as a marker; clicking downloads / previews it.',
							'Photo EXIF shooting time is preserved as the "Shooting" field, sortable in the data list.',
						],
					},
					{
						icon: I.datalist,
						title: 'Registration limits',
						body: [
							'A single map holds up to 150 markers and photos combined, and up to 50 uploaded files (GPX tracks, KML, GeoJSON, ZIP and so on).',
							'The two limits are independent: even with 150 markers and photos, you can still add files such as tracks up to 50.',
							'Freehand strokes, shapes and labels do not count toward either limit — they are stored together per user, so drawing more of them never consumes the quota.',
							'Once a limit is reached, adding markers or uploading is blocked and a message appears. Bulk imports of GPX waypoints or photos stop at the limit.',
							'Check the current counts in the data list, and delete markers, photos or files you no longer need.',
						],
					},
					{
						icon: I.print,
						title: 'Print / PDF',
						body: [
							'The print dialog covers paper size (A3/A4/B4/B5), orientation, scale (1:10,000–1:200,000 or custom), scale-bar position, color (color/mono/mono+red), magnetic-north line count and color, QR position (bottom-right/bottom-left/none), marker mode (none / as is / convert to diamond), marker size (75/100/150%) and text-size adjustment.',
							'Drag the blue rectangle on the map to position the print area.',
							'The output prints title/owner/note at top-left, scale and paper size at top-right, a QR code (deep link to map center) at bottom-right or bottom-left, and tile credits along the page edge.',
							'Rendering is done at ~300 DPI so even A3 prints stay crisp.',
						],
					},
					{
						icon: I.share,
						title: 'Sharing',
						body: [
							'Switch among Private / View-only / Joint editing / Searchable in the share dialog.',
							'Joint editing supports multiple simultaneous editors without conflicts; each user\'s strokes/markers are stored independently.',
						],
					},
					{
						icon: I.chat,
						title: 'Chat',
						body: [
							'Real-time text chat for users on the same map.',
						],
					},
				],
			},
			{
				id: 'qa',
				title: 'FAQ',
				body: [],
				sub: [
					{
						title: 'Q. When does anonymous-account data disappear?',
						body: ['A. 30 days after the last sign-in. Sign in with Google (make permanent) before then to keep all your data.'],
					},
					{
						title: 'Q. Can I view maps offline?',
						body: ['A. When installed as a PWA, recently viewed tiles are cached and viewable offline. Saving new data still requires an internet connection.'],
					},
					{
						title: 'Q. I want to undo my last edit.',
						body: ['A. Use the undo button (Ctrl/Cmd+Z). Repeated presses keep undoing further actions. If you went too far, the redo button (Ctrl/Cmd+Y) reapplies what you just undid.'],
					},
					{
						title: 'Q. How do I make markers easier to read when printing?',
						body: ['A. Set "Marker printing" to "Convert to diamond" in the print dialog to render every marker as a diamond. Use "Marker size" to choose 75/100/150%. With color mode "mono+red" all markers become red diamonds; otherwise diamond color follows iconNumber mod 6. Photo markers are omitted in this mode, since a diamond would not show their content.'],
					},
					{
						title: 'Q. Do simultaneous edits collide in joint editing mode?',
						body: ['A. No — each user\'s data is stored independently, so editing the same area at once never conflicts. Edits propagate in real time.'],
					},
					{
						title: 'Q. My photo is at the wrong place.',
						body: ['A. Without EXIF GPS, photos drop at the screen center. Open the marker popup, press Move, then tap the correct location.'],
					},
					{
						title: 'Q. I want to see the terrain in 3D.',
						body: ['A. Toggle 2D/3D from the right-hand toolbar. The Settings dialog also lets you change the terrain exaggeration.'],
					},
					{
						title: 'Q. I can no longer add markers or files.',
						body: ['A. You have hit a registration limit: 150 markers and photos combined, and 50 uploaded files such as tracks. The two are counted separately. Check the counts in the data list and delete what you no longer need. Freehand strokes, shapes and labels are not counted.'],
					},
				],
			},
		],
	},
};

const HelpPage = () => {
	const navigate = useNavigate();
	const [lang, setLang] = useState(detectInitialLang);
	useEffect(() => {
		try { localStorage.setItem('helpPageLang', lang); } catch {}
	}, [lang]);
	useEffect(() => {
		const prev = document.title;
		document.title = lang === 'ja' ? 'CollaboMap 使い方ガイド' : 'CollaboMap User Guide';
		return () => { document.title = prev; };
	}, [lang]);

	const content = useMemo(() => HELP_CONTENT[lang] || HELP_CONTENT.en, [lang]);

	return (
		<div className="help-page">
			<div className="help-toolbar">
				<button className="help-back-btn" onClick={() => navigate(-1)}>
					← {content.backToTop}
				</button>
				<h1 className="help-title">{content.pageTitle}</h1>
				<button
					className="help-lang-btn"
					onClick={() => setLang(l => (l === 'ja' ? 'en' : 'ja'))}
					title={content.toggleLabel}
				>
					{content.toggleLabel}
				</button>
			</div>

			<nav className="help-toc">
				<strong>{content.toc}</strong>
				<ul>
					{content.sections.map(s => (
						<li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>
					))}
				</ul>
			</nav>

			<main className="help-body">
				{content.sections.map(section => (
					<section key={section.id} id={section.id} className="help-section">
						<h2>{section.title}</h2>
						{section.body && section.body.map((p, i) => (
							<p key={i}>{p}</p>
						))}
						{section.image && (
							<figure className="help-section-image">
								<img src={section.image.src} alt={section.image.alt || ''} />
							</figure>
						)}
						{section.features && section.features.map((feat, i) => (
							<div key={i} className="help-feature">
								{feat.buttonLabel ? (
									<span className="help-feature-button" aria-hidden="true">{feat.buttonLabel}</span>
								) : feat.icon && (
									<img className="help-feature-icon" src={feat.icon} alt="" aria-hidden="true" />
								)}
								<div className="help-feature-text">
									<h3>{feat.title}</h3>
									{feat.body.map((p, j) => (
										<p key={j}>{p}</p>
									))}
								</div>
							</div>
						))}
						{section.sub && section.sub.map((sub, i) => (
							<div key={i} className="help-subsection">
								<h3>{sub.title}</h3>
								{sub.body.map((p, j) => (
									<p key={j}>{p}</p>
								))}
							</div>
						))}
					</section>
				))}
			</main>

			<footer className="help-footer">
				<button className="help-back-btn" onClick={() => navigate(-1)}>
					← {content.backToTop}
				</button>
			</footer>
		</div>
	);
};

export default HelpPage;
